import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paiementsApi, type Paiement } from '../services/api';
import { useToast } from '../context/ToastContext';
import { Spinner } from '../components/shared/Spinner';
import { PdfPreviewDrawer } from '../components/paiements/PdfPreviewDrawer';
import { PaiementManuelModal } from '../components/paiements/PaiementManuelModal';
import { ReponseMotifRefus } from '../components/paiements/ReponseMotifRefus';
import { confirmationSansReponse, lignesSansReponse } from '../lib/reponse-attendue';
import { formatApiError } from '../lib/api-error';
import { useValideursPaiements } from '../hooks/useValideursPaiements';
import {
  ChoixValideur,
  EtatListeValideurs,
  repartitionParValideur,
  valideurCommun,
} from '../components/paiements/ChoixValideur';
import { nomDuValideur, valideurEffectif } from '../../server/lib/valideurs';
import { BANQUES_VIREMENT } from '../../server/lib/banques-virement';
import { avertissementsCoordonnees } from '../lib/saisie-paiement';

/**
 * Module Paiements — écran « À payer » (Valérie / admin).
 * Synchro Zoho → liste groupée par type de charge → choix banque/date →
 * soumission pour validation, chaque ligne à son valideur (Dominique par
 * défaut), choisi pour tout, par section ou ligne par ligne.
 */

function eur(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export function PaiementsAPayerPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Aperçu PDF : index dans la liste plate `rows` (null = panneau fermé).
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [manuelOpen, setManuelOpen] = useState(false);
  const [virementOpen, setVirementOpen] = useState(false);
  // Valideur choisi à l'écran, par ligne ; sans choix, la ligne garde le sien
  // (celui qui l'a refusée) ou prend le valideur par défaut.
  const [choixValideur, setChoixValideur] = useState<Record<string, string>>({});
  const valideursQ = useValideursPaiements();
  const valideurs = valideursQ.data?.valideurs ?? [];

  const q = useQuery({
    queryKey: ['paiements', 'a-payer'],
    queryFn: () => paiementsApi.aPayer(),
    staleTime: 30_000,
  });

  const syncMutation = useMutation({
    mutationFn: () => paiementsApi.sync(),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['paiements'] });
      let msg = `Synchro Zoho : ${r.created} ajouté(s), ${r.updated} mis à jour (${r.total_virement} en virement).`;
      if (r.reprises) {
        msg += ` ${r.reprises} solde(s) restant(s) dû(s) après correction dans Books.`;
      }
      toast.success(msg);
    },
    onError: (err) => toast.error(`Synchro échouée : ${formatApiError(err)}`),
  });

  const updateMutation = useMutation({
    mutationFn: (v: {
      id: string;
      banque?: string | null;
      date_paiement?: string | null;
      reponse_admin?: string | null;
    }) =>
      paiementsApi.update(v.id, {
        banque: v.banque,
        date_paiement: v.date_paiement,
        reponse_admin: v.reponse_admin,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['paiements', 'a-payer'] }),
    onError: (err) => toast.error(`Modification échouée : ${formatApiError(err)}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => paiementsApi.supprimer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['paiements'] });
      toast.success('Ligne supprimée.');
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const submitMutation = useMutation({
    mutationFn: (ids: string[]) =>
      paiementsApi.submit(ids.map((id) => ({ id, valideur_email: valideurDe(id) || undefined }))),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['paiements'] });
      setSelected(new Set());
      setChoixValideur({});
      let msg = `${r.submitted} paiement(s) soumis : ${repartitionParValideur(r.par_valideur ?? [])}.`;
      if (!r.email_sent) msg += ' (email non envoyé : SMTP non configuré)';
      if (r.blocked.length) msg += ` ${r.blocked.length} bloqué(s).`;
      toast.success(msg);
    },
    // `formatApiError` et non `String(err)` : la soumission renvoie depuis le
    // 14/10/2026 un refus détaillé (« Valideur inconnu », avec le champ fautif),
    // que `String(err)` réduisait à « Error: … » — illisible.
    onError: (err) => toast.error(`Soumission échouée : ${formatApiError(err)}`),
  });

  const rows = q.data?.paiements ?? [];

  /** Le valideur qui recevra la ligne si on la soumet maintenant. */
  function valideurDe(id: string): string {
    if (valideurs.length === 0) return '';
    const p = rows.find((x) => x.id === id);
    return choixValideur[id] ?? valideurEffectif(p?.valideur_email, valideurs);
  }
  function choisirValideur(ids: string[], email: string) {
    setChoixValideur((prev) => {
      const next = { ...prev };
      for (const id of ids) next[id] = email;
      return next;
    });
  }
  const compteParValideur = new Map<string, number>();
  for (const id of selected) compteParValideur.set(valideurDe(id), (compteParValideur.get(valideurDe(id)) ?? 0) + 1);
  const selectionParValideur = [...compteParValideur].map(([email, lignes]) => ({
    nom: nomDuValideur(email, valideurs),
    lignes,
  }));
  const plusieursValideurs = valideurs.length > 1;

  // Groupage par type de charge.
  const groups = useMemo(() => {
    const m = new Map<string, Paiement[]>();
    for (const p of rows) {
      const k = p.type_charge ?? 'Sans type de charge';
      (m.get(k) ?? m.set(k, []).get(k)!).push(p);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  const selectedTotal = rows.filter((p) => selected.has(p.id)).reduce((s, p) => s + p.montant, 0);

  /**
   * Soumission : on alerte quand une ligne refusée repart SANS réponse au
   * motif. Le motif est souvent une question — la renvoyer telle quelle
   * expose à un second refus pour la même raison.
   */
  function handleSubmit() {
    const ids = [...selected];
    // La détection et le texte vivent dans `src/lib/reponse-attendue.ts`, pur et
    // testé : l'écran des avoirs pose la même question avec les mots de SON
    // objet (« avoir refusé »), et deux copies auraient divergé.
    const texte = confirmationSansReponse(lignesSansReponse(rows, selected), {
      objet: 'facture',
      refuses: 'refusée(s)',
      tiers: (p) => p.vendor_name,
      numero: (p) => p.bill_number,
    });
    if (texte && !window.confirm(texte)) return;
    submitMutation.mutate(ids);
  }

  function toggle(p: Paiement) {
    if (p.iban_manquant || !p.banque) return; // non sélectionnable
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(p.id)) next.delete(p.id);
      else next.add(p.id);
      return next;
    });
  }

  // ─── Sélection en masse (global + par bloc de charge) ───────────────────
  // Sélectionnable = bénéficiaire connu (IBAN) + banque choisie.
  const isSelectable = (p: Paiement) => !p.iban_manquant && !!p.banque;
  function setSelection(ids: string[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) (on ? next.add(id) : next.delete(id));
      return next;
    });
  }
  const selectableRows = rows.filter(isSelectable);
  const allSelected = selectableRows.length > 0 && selectableRows.every((p) => selected.has(p.id));
  const someSelected = selectableRows.some((p) => selected.has(p.id));

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Factures à payer</h1>
          <p className="text-sm text-slate-500 mt-1">
            Factures Zoho impayées en virement, à préparer puis soumettre au valideur choisi.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setManuelOpen(true)}
            className="px-3 py-1.5 rounded-md text-sm text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50"
            title="Ajouter une facture qui ne remonte pas de la synchro Zoho"
          >
            + Saisir une facture
          </button>
          <button
            type="button"
            onClick={() => setVirementOpen(true)}
            className="px-3 py-1.5 rounded-md text-sm text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50"
            title="Transférer de l'argent entre deux comptes KESTIO"
          >
            + Virement interne
          </button>
          <button
            type="button"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="px-3 py-1.5 rounded-md text-sm bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {syncMutation.isPending && <Spinner />}
            Synchroniser depuis Zoho
          </button>
        </div>
      </div>

      {avertissementsCoordonnees(rows).map((message) => (
        <div key={message} className="rounded-md bg-amber-50 text-amber-800 px-4 py-2.5 text-sm">
          ⚠ {message}
        </div>
      ))}

      <EtatListeValideurs
        erreur={valideursQ.isError ? valideursQ.error : null}
        chargement={valideursQ.isLoading}
        relanceEnCours={valideursQ.isFetching}
        onReessayer={() => valideursQ.refetch()}
        objet="facture"
      />

      {q.isLoading ? (
        <p className="text-slate-500">Chargement…</p>
      ) : q.isError ? (
        <p className="text-[#C0423F]">Erreur : {formatApiError(q.error)}</p>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
          Aucune facture à payer. Lance une synchro Zoho.
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map(([charge, items]) => {
            const total = items.reduce((s, p) => s + p.montant, 0);
            const groupSelectable = items.filter(isSelectable);
            const groupAll = groupSelectable.length > 0 && groupSelectable.every((p) => selected.has(p.id));
            const groupSome = groupSelectable.some((p) => selected.has(p.id));
            return (
              <div key={charge} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between bg-slate-50 px-4 py-2 border-b border-slate-200">
                  <label className="flex items-center gap-2 font-medium cursor-pointer">
                    <TriCheckbox
                      checked={groupAll}
                      indeterminate={!groupAll && groupSome}
                      disabled={groupSelectable.length === 0}
                      onChange={() => setSelection(groupSelectable.map((p) => p.id), !groupAll)}
                    />
                    {charge}
                    <span className="text-xs font-normal text-slate-400">
                      ({groupSelectable.length} sélectionnable{groupSelectable.length > 1 ? 's' : ''})
                    </span>
                  </label>
                  <div className="flex items-center gap-3">
                    {plusieursValideurs && (
                      <ChoixValideur
                        valideurs={valideurs}
                        value={valideurCommun(items.map((p) => valideurDe(p.id)))}
                        onChange={(email) => choisirValideur(items.map((p) => p.id), email)}
                        label={`Valideur de la section ${charge}`}
                      />
                    )}
                    <span className="text-sm text-slate-600">Total : {eur(total)}</span>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-slate-500 text-left text-xs uppercase">
                    <tr>
                      <th className="px-3 py-2 w-8"></th>
                      <th className="px-3 py-2 font-medium">Fournisseur</th>
                      <th className="px-3 py-2 font-medium">N° facture</th>
                      <th className="px-3 py-2 font-medium text-right">Montant</th>
                      <th className="px-3 py-2 font-medium">Échéance</th>
                      <th className="px-3 py-2 font-medium">Date paiement</th>
                      <th className="px-3 py-2 font-medium">Banque</th>
                      <th className="px-3 py-2 font-medium">Valideur</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((p) => (
                      <tr key={p.id} className={p.iban_manquant ? 'bg-amber-50/40' : ''}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selected.has(p.id)}
                            disabled={p.iban_manquant || !p.banque}
                            onChange={() => toggle(p)}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium">
                          {p.vendor_name}
                          {p.iban_manquant && (
                            <span className="ml-2 text-xs text-amber-600" title="Bénéficiaire / IBAN introuvable">⚠ IBAN manquant</span>
                          )}
                          {p.origine === 'reprise' && (
                            <span
                              className="ml-2 text-xs text-blue-700 bg-blue-50 rounded px-1.5 py-0.5"
                              title={p.origine_motif ?? undefined}
                            >
                              ↻ Solde dû
                            </span>
                          )}
                          {p.origine === 'manuel' && (
                            <span
                              className="ml-2 text-xs text-slate-700 bg-slate-100 rounded px-1.5 py-0.5"
                              title={p.origine_motif ?? undefined}
                            >
                              ✎ Saisie manuelle
                            </span>
                          )}
                          {p.origine === 'virement_interne' && (
                            <span className="ml-2 text-xs text-slate-700 bg-slate-100 rounded px-1.5 py-0.5">
                              ⇄ Virement interne
                            </span>
                          )}
                          {p.origine !== 'zoho' && p.origine_motif && (
                            <div className="mt-0.5 text-xs text-slate-500">{p.origine_motif}</div>
                          )}
                          {p.rejet_motif && (
                            <div className="mt-1 rounded bg-red-50 ring-1 ring-inset ring-red-100 px-2 py-1.5">
                              <div className="text-xs text-red-700">⨯ Refusé : {p.rejet_motif}</div>
                              <ReponseMotifRefus
                                reponse={p.reponse_admin}
                                reponseAt={p.reponse_at}
                                valideur={nomDuValideur(valideurDe(p.id), valideurs) || 'valideur'}
                                onSave={(reponse) => updateMutation.mutate({ id: p.id, reponse_admin: reponse })}
                              />
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{p.bill_number}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{eur(p.montant)}</td>
                        <td className="px-3 py-2 text-slate-500">{p.date_echeance ?? '-'}</td>
                        <td className="px-3 py-2">
                          <input
                            type="date"
                            value={p.date_paiement ?? ''}
                            disabled={p.origine === 'virement_interne'}
                            onChange={(e) => updateMutation.mutate({ id: p.id, date_paiement: e.target.value || null })}
                            className="rounded border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-100"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={p.banque ?? ''}
                            disabled={p.origine === 'virement_interne'}
                            onChange={(e) => updateMutation.mutate({ id: p.id, banque: e.target.value || null })}
                            className={`rounded border px-2 py-1 text-xs bg-white disabled:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 ${p.banque ? 'border-slate-300' : 'border-amber-400'}`}
                          >
                            <option value="">Choisir</option>
                            {BANQUES_VIREMENT.map((b) => (
                              <option key={b} value={b}>{b}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          {plusieursValideurs ? (
                            <ChoixValideur
                              valideurs={valideurs}
                              value={valideurDe(p.id)}
                              onChange={(email) => choisirValideur([p.id], email)}
                              label={`Valideur de la facture ${p.bill_number}`}
                            />
                          ) : (
                            <span className="text-xs text-[#384872]">
                              {nomDuValideur(valideurDe(p.id), valideurs)}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {p.origine !== 'virement_interne' && (
                            <button
                              type="button"
                              onClick={() => setPreviewIndex(rows.findIndex((x) => x.id === p.id))}
                              title="Voir la facture"
                              className="px-2.5 py-1 rounded-md text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                            >
                              👁 Voir
                            </button>
                          )}
                          {p.origine !== 'zoho' && p.origine !== undefined && (
                            <button
                              type="button"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Supprimer la ligne ${p.vendor_name} : ${p.bill_number} (${eur(p.montant)}) ?`,
                                  )
                                ) {
                                  deleteMutation.mutate(p.id);
                                }
                              }}
                              title="Supprimer cette ligne (saisie manuelle ou solde repris)"
                              className="ml-1.5 px-2 py-1 rounded-md text-xs text-slate-500 ring-1 ring-slate-200 hover:bg-red-50 hover:text-red-600"
                            >
                              🗑
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          {/* Barre d'action */}
          <div className="sticky bottom-0 bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <TriCheckbox
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  disabled={selectableRows.length === 0}
                  onChange={() => setSelection(selectableRows.map((p) => p.id), !allSelected)}
                />
                Tout sélectionner
              </label>
              <span className="text-sm text-slate-600">
                {selected.size} sélectionné(s) · {eur(selectedTotal)}
              </span>
              {plusieursValideurs && (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  Valideur de toutes les factures :
                  <ChoixValideur
                    valideurs={valideurs}
                    value={valideurCommun(rows.map((p) => valideurDe(p.id)))}
                    onChange={(email) => choisirValideur(rows.map((p) => p.id), email)}
                    label="Valideur de toutes les factures"
                  />
                </label>
              )}
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={selected.size === 0 || submitMutation.isPending}
              className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {submitMutation.isPending && <Spinner />}
              Soumettre à validation
              {selected.size > 0 && valideurs.length > 0 && ` (${repartitionParValideur(selectionParValideur)})`}
            </button>
          </div>
        </div>
      )}

      {manuelOpen && <PaiementManuelModal onClose={() => setManuelOpen(false)} />}
      {virementOpen && <PaiementManuelModal mode="virement-interne" onClose={() => setVirementOpen(false)} />}

      {/* Panneau latéral : aperçu PDF de la facture. */}
      {previewIndex != null && rows[previewIndex] && (
        <PdfPreviewDrawer
          paiement={rows[previewIndex]!}
          onClose={() => setPreviewIndex(null)}
          onPrev={() => setPreviewIndex((i) => (i != null && i > 0 ? i - 1 : i))}
          onNext={() => setPreviewIndex((i) => (i != null && i < rows.length - 1 ? i + 1 : i))}
          hasPrev={previewIndex > 0}
          hasNext={previewIndex < rows.length - 1}
        />
      )}
    </div>
  );
}

/** Case à cocher tri-état (supporte l'état « indéterminé » via ref). */
function TriCheckbox({
  checked,
  indeterminate,
  disabled,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
    />
  );
}
